import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { InsiderBadge } from "@faire/retailer-visitor-shared/components/InsiderBadge/InsiderBadge";
import { useInsiderFeatureEligibility } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderFeatureEligibility";
import { useInsiderMembershipInformation } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderMembershipInformation";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { Container, TextSkeleton } from "@faire/retailer-visitor-shared/views/user/InsiderMarketing/components/InsiderHowToUse/__internal__/InsiderHowToUseCardAssets/styled";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { moneyOrZero, useFormatMoney } from "@faire/web--source/common/money";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Row } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useQuery as useInsiderMarketingPageData } from "@faire/web-api--source/api/retailer/insider/marketing-page/get-hooks";
import React from "react";
import styled from "styled-components";
export var DiscoverBrands = function() {
    var isSSR = useIsSSR();
    var FIRST_TILE_IMAGE = optimizeImageUrl({
        url: "https://cdn.faire.com/fastly/4946673ecd3821ea459ee52daf58e7832bd7389d4ba55efa3328a0ce085630e7.png"
    }, isSSR, {
        width: 199,
        height: 199
    });
    var SECOND_TILE_IMAGE = optimizeImageUrl({
        url: "https://cdn.faire.com/fastly/8a4dec95e6943a28e7037bdba6523ed69f66e63735c5a0aa5a569a8451bec909.png"
    }, isSSR, {
        width: 199,
        height: 199
    });
    return /*#__PURE__*/ _jsx(Container, {
        children: /*#__PURE__*/ _jsxs(ContentWrapper, {
            children: [
                /*#__PURE__*/ _jsx(Tile, {
                    imgSrc: FIRST_TILE_IMAGE,
                    isFirst: true
                }),
                /*#__PURE__*/ _jsx(Tile, {
                    imgSrc: SECOND_TILE_IMAGE
                })
            ]
        })
    });
};
var ContentWrapper = styled(Row).attrs({
    align: "flex-end"
}).withConfig({
    componentId: "sc-74cd6d3c-0"
})([
    "transform:scale(0.65);",
    "{transform:unset;flex:1;}"
], responsiveMediaQueries.desktopAndAbove);
var Tile = function(param) {
    var imgSrc = param.imgSrc, isFirst = param.isFirst;
    var _insiderMarketingPageData_logged_out_info_threshold_values, _insiderMarketingPageData_logged_out_info, _insiderMarketingPageData_logged_out_info_threshold_values1, _insiderMarketingPageData_logged_out_info1;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var formatMoney = useFormatMoney().formatMoney;
    var _useInsiderMembershipInformation = useInsiderMembershipInformation(), _useInsiderMembershipInformation_thresholds = _useInsiderMembershipInformation.thresholds, loggedInInsiderThresholdT1 = _useInsiderMembershipInformation_thresholds.threshold1, loggedInInsiderThresholdT2 = _useInsiderMembershipInformation_thresholds.threshold2;
    var hasFreeDutyAdvertising = useInsiderFeatureEligibility().hasFreeDutyAdvertising;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var _useInsiderMarketingPageData = useInsiderMarketingPageData({
        enabled: !isLoggedInRetailer
    }), insiderMarketingPageData = _useInsiderMarketingPageData.data;
    var loggedOutInsiderThresholdT1 = insiderMarketingPageData === null || insiderMarketingPageData === void 0 ? void 0 : (_insiderMarketingPageData_logged_out_info = insiderMarketingPageData.logged_out_info) === null || _insiderMarketingPageData_logged_out_info === void 0 ? void 0 : (_insiderMarketingPageData_logged_out_info_threshold_values = _insiderMarketingPageData_logged_out_info.threshold_values) === null || _insiderMarketingPageData_logged_out_info_threshold_values === void 0 ? void 0 : _insiderMarketingPageData_logged_out_info_threshold_values.THRESHOLD_1;
    var loggedOutInsiderThresholdT2 = insiderMarketingPageData === null || insiderMarketingPageData === void 0 ? void 0 : (_insiderMarketingPageData_logged_out_info1 = insiderMarketingPageData.logged_out_info) === null || _insiderMarketingPageData_logged_out_info1 === void 0 ? void 0 : (_insiderMarketingPageData_logged_out_info_threshold_values1 = _insiderMarketingPageData_logged_out_info1.threshold_values) === null || _insiderMarketingPageData_logged_out_info_threshold_values1 === void 0 ? void 0 : _insiderMarketingPageData_logged_out_info_threshold_values1.THRESHOLD_2;
    var threshold1 = isLoggedInRetailer ? loggedInInsiderThresholdT1 : loggedOutInsiderThresholdT1;
    var threshold2 = isLoggedInRetailer ? loggedInInsiderThresholdT2 : loggedOutInsiderThresholdT2;
    var hasTwoThresholds = !!(threshold1 && threshold2);
    return /*#__PURE__*/ _jsxs(TilePlaceholder, {
        children: [
            /*#__PURE__*/ _jsx(Image, {
                src: imgSrc
            }),
            /*#__PURE__*/ _jsx(TextSkeleton, {
                margin: getSpacing("2x", 0, "1x"),
                width: "152px",
                stroke: "18px"
            }),
            /*#__PURE__*/ _jsx(InsiderBadge, {
                showFreeShipping: true,
                showFreeDuties: isFirst && hasFreeDutyAdvertising,
                shouldUseFallbackText: !hasTwoThresholds && (isFirst || !threshold1),
                insiderBadgeText: hasTwoThresholds ? strictLocalize({
                    id: "KCobuI",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Free shipping over "
                        },
                        {
                            type: 1,
                            value: "freeShippingThresholdMoney"
                        },
                        {
                            type: 0,
                            value: " with Insider"
                        }
                    ]
                }, {
                    freeShippingThresholdMoney: formatMoney({
                        money: moneyOrZero(isFirst ? threshold1 : threshold2),
                        omitCentsIfZero: true
                    })
                }) : strictLocalize({
                    id: "LkrMVb",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Free shipping with Insider"
                        }
                    ]
                }),
                isInsiderMarketingPage: true
            })
        ]
    });
};
var TilePlaceholder = styled.div.withConfig({
    componentId: "sc-74cd6d3c-1"
})([
    "width:199px;min-height:290px;background-color:",
    ";padding:",
    ";margin:",
    ";& + &{margin:",
    ";}"
], Core.surface.inverse, getSpacing(0, "2x", "1x"), getSpacing(0, 0, 73), getSpacing(0, 0, 0, 12));
var Image = styled.img.withConfig({
    componentId: "sc-74cd6d3c-2"
})([
    "position:relative;left:-16px;width:199px;height:199px;"
]);
